// Build 1 (session 60) — config for the "expandable" section layout.
//
// A section on layoutType "expandable" renders a compact basic-field row per
// record that expands into the full record (grouped fields + Notes + Docs).
// This config, keyed by section slug, declares which fields are basic (shown
// on the collapsed row) and how the remaining fields group in the expanded
// panel. Build 1 supplies ONE entry — Vehicle List. The renderer is generic;
// rollout to other sections is a later workstream.
//
// Design + scope: docs/archive/cc-prompts/2026-05-24_session60-build1-vehicle-list-layout.md
// Visual ref: docs/archive/working-notes/2026-05-24/ (Vehicle List Layout Mockup v3)

import { SECTION_SLUGS } from "@shared/constants/sections";

/**
 * s100 (D-100.x): per-section completion mode for the one-list shaded-critical-rows
 * model. Opt-in — a section absent from this map keeps the legacy "≥1 complete
 * user-touched row" rule. The Phase-2 fan-out extends this map per section.
 *   'shaded'  — has critical rows; complete when EVERY shaded row resolves
 *               (filled or declined). Shaded membership = locked / essential-tied /
 *               curated (TOP_ZONE_SCAFFOLDS), computed by the completion adapter.
 *   'engaged' — a pure record-list with no shaded set; complete when ≥1 user-touched
 *               row exists, OR the section is marked "none / doesn't apply".
 *
 * s159 (Option A): NO section declares 'engaged' any more. The mode survives only as the
 * in-engine fallback for a 'shaded' section whose shaded rows have all been deleted
 * (`computeSectionProgress`). A DECLARED 'engaged' entry short-circuits D-156 strict list
 * scoring, which is exactly the drift that took children out of bar/lens agreement — so a
 * new entry here should be 'shaded' or absent, never 'engaged'.
 */
export const SECTION_COMPLETION_MODES: Record<string, "shaded" | "engaged"> = {
  // s100 Phase 1 proof consumer.
  [SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS]: "shaded",
  // s101 Phase 3 fan-out — every multi-record section with a curated shaded set.
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES]: "shaded",
  [SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP]: "shaded",
  [SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES]: "shaded",
  // s101 post-verification: Income's Employment default row now seeds, so it shades
  // like its siblings (curation on inc_type, exact-match "Employment").
  [SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS]: "shaded",
  [SECTION_SLUGS.LEGAL_POWERS_OF_ATTORNEY]: "shaded",
  [SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS]: "shaded",
  [SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS]: "shaded",
  [SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS]: "shaded",
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]: "shaded",
  [SECTION_SLUGS.ASSETS_PROPERTIES]: "shaded",
  [SECTION_SLUGS.ASSETS_VEHICLE_LIST]: "shaded",
  [SECTION_SLUGS.PERSONAL_INFORMATION_CAREER]: "shaded",
  // s106 (MIG-1 / DRA-1): Health Insurance shades its Extended Health + Dental starters
  // (curation on plan_type — see TOP_ZONE_CURATION_FIELD). Seeded rows delivered to
  // clients via template_version bump + heal (heal-first).
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_INSURANCE]: "shaded",
  // DB-9: Pets shades its curated "Your pet" starter row (TOP_ZONE_SCAFFOLDS); seeded
  // pet_name default + template_version bump (heal-first) deliver the row to clients.
  [SECTION_SLUGS.FAMILY_PETS]: "shaded",
  // s101 post-verification: reconcile a pre-existing s99 inconsistency — Your Executor
  // already renders sage-shaded Primary/Alternate rows (TOP_ZONE) but completed in
  // legacy mode. Promote to shaded so the pill matches the shading. The
  // executor_identified essential reads `name` via ESSENTIAL_CHECKS (server + spec),
  // independent of SECTION_COMPLETION_MODES — unaffected.
  [SECTION_SLUGS.LEGAL_YOUR_EXECUTOR]: "shaded",
  // s159 (Option A, ruled): children-dependents-overview is deliberately ABSENT. Its s108
  // 'engaged' mode ("complete when ≥1 row is touched") was never elevated to a decision and
  // short-circuited D-156 strict scoring, so the bar could read complete while the planner
  // lens still listed unresolved child rows — a violation of the s158 bar-lens agreement
  // ruling. It now scores under D-156 strict like every other list section: every visible
  // row filled or declined. Bar and lens must agree.
};

export interface ExpandableGroup {
  /** Heading shown above the group in the expanded panel. */
  groupName: string;
  /** Field keys in display order. */
  fieldKeys: string[];
  /** Optional muted helper text rendered directly under the group header (brand
   *  helper-text style). Use for will-consistency nudges and similar guidance.
   *  Groups without it render nothing extra. */
  groupNote?: string;
}

/**
 * Inline-row slot width for a basic field. Drives the collapsed/expanded header
 * grid track so the renderer sizes the inline row FROM CONFIG rather than from a
 * hard-coded, per-section grid template — keeps the layout content-agnostic.
 *   narrow   — short values (year, codes)
 *   flex     — the long descriptive field (grows to fill the row)
 *   currency — fixed money-width column
 *   default  — modest fixed slot (selects, short labels); used when unspecified
 */
export type BasicFieldWidth = "narrow" | "flex" | "currency" | "default";

/**
 * Per-field column SPAN for grouped fields in the EXPANDED DRAWER. The drawer is
 * a responsive CSS grid (4 cols at full width → 2 at laptop → 1 narrow); each
 * field occupies a whole number of cells, so inputs align on a shared grid.
 * Extends the same config-driven idea as `basicFieldWidths` (which sizes the
 * inline row) to the drawer.
 *   1    — one cell (short codes, dates)
 *   2    — two cells (names, locations)
 *   full — span the whole row (long-form fields)
 * Span is clamped to the available column count at narrow breakpoints, so a
 * span-2 / full field never overflows. Keys absent fall back to 1.
 */
export type DrawerFieldSpan = 1 | 2 | "full";

export interface ExpandableLayoutConfig {
  /** Singular, lowercase record noun (e.g. "vehicle"). Required — drives the
   *  empty-state and delete/confirm copy so the shared renderer carries no
   *  per-section wording. Capitalize at the use site if needed. */
  recordNoun: string;
  /** Optional plural override for irregular plurals. Defaults to
   *  `recordNoun + "s"`. */
  recordNounPlural?: string;
  /** Field keys shown on the collapsed row, in display order. */
  basicFieldKeys: string[];
  /** Per-basic-field inline slot width. Keys absent here fall back to "default".
   *  The renderer builds the grid template from this — no hard-coded widths. */
  basicFieldWidths?: Record<string, BasicFieldWidth>;
  /** Non-basic fields grouped for the expanded panel, in display order. */
  groups: ExpandableGroup[];
  /** The Notes field key (rendered full-width in the panel), or null. */
  notesFieldKey: string | null;
  /** Per-field drawer column span for grouped fields (1 | 2 | full). Keys absent
   *  → span 1. The renderer builds a responsive grid from these. */
  fieldSpans?: Record<string, DrawerFieldSpan>;
  /** When set, the expanded drawer shows a "copy from another record" control
   *  (config-gated — renders only where present). `label` is the link text;
   *  `fields` are the field keys copied as a one-time SNAPSHOT (not a live link)
   *  from a chosen sibling record into the current one. NEVER include identity
   *  (`name`) or `notes`. Currently used on expandable sections only, whose rows
   *  are always real DB rowIds; a future TABLE consumer would also need virtual-
   *  row resolution in the parent's onSaveFieldValue. */
  copyFromSibling?: { label: string; fields: string[] };
  /** Optional per-field, per-value muted helper line shown below a field's input in
   *  the drawer — keyed `valueHelp[fieldKey][currentValue]`. Used to explain what a
   *  selected option means (e.g. executor Access Level). Absent → no helper. */
  valueHelp?: Record<string, Record<string, string>>;
  /** Optional section-HEADER field (s95, Option 2): a single fact for the whole
   *  section (e.g. Organ Donor Status on Health & Medical), rendered prominently
   *  ABOVE the record list rather than per-record. Its value lives on a dedicated
   *  `__header__<slug>` row (created lazily on first save — no seeding) and is
   *  excluded from the per-record ribbon/drawer + the record list. By default it is
   *  the section's completion criterion (see computeSectionProgress); set
   *  `drivesCompletion: false` to render the band WITHOUT it driving completion
   *  (s99 — Funeral's band captures burial/cremation but completion stays on
   *  `wishes`). The field must NOT also appear in `basicFieldKeys`/`groups`. */
  headerField?: { fieldKey: string; label?: string; helpText?: string; drivesCompletion?: boolean };
}

/**
 * The EditableCell `type` prop that a given fieldType maps to. Mirrors the
 * prop derivation the existing table renderer performs in GenericCategoryPage
 * (select is driven by `options`, not by this type; multiline is a separate
 * flag — both handled by the renderer, not here).
 */
export type CellInputType = "text" | "currency" | "date" | "password" | "tel" | "email" | "number" | "yes_no";

export function deriveCellInputType(fieldType: string): CellInputType {
  switch (fieldType) {
    case "yes_no":
      return "yes_no";
    case "password":
    case "pin":
      return "password";
    case "phone":
      return "tel";
    case "email":
      return "email";
    case "currency":
      return "currency";
    case "date":
      return "date";
    case "number":
      return "number";
    default:
      return "text";
  }
}

export const EXPANDABLE_LAYOUT_CONFIG: Record<string, ExpandableLayoutConfig> = {
  // s94 (D-94.1–6): Employment History — re-activated Career as a multi-record
  // employer list (feeds the employer notification letters). Status/Employer/Title
  // on the ribbon; Contact + Dates in the drawer.
  [SECTION_SLUGS.PERSONAL_INFORMATION_CAREER]: {
    recordNoun: "employer",
    // s101: hr_contact_phone promoted to the ribbon; job_title moves to the Role drawer group.
    basicFieldKeys: ["employer", "employment_status", "hr_contact_phone"],
    basicFieldWidths: { employer: "flex", employment_status: "flex", hr_contact_phone: "default" },
    groups: [
      // s237 chapter three PR 2 (FIELD-BEN-1): the Work screen's employee number and row kind, in the drawer.
      { groupName: "Role", fieldKeys: ["job_title", "employee_number", "career_kind"] },
      {
        groupName: "Contact",
        // s237 chapter three PR 2 (FIELD-BEN-1): the employer company card's phone / email and the HR contact's address.
        fieldKeys: ["employer_address", "employer_phone", "employer_email", "hr_contact_name", "hr_contact_email", "hr_contact_address"],
      },
      { groupName: "Dates", fieldKeys: ["start_year", "end_year"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { employer_address: 2, hr_contact_name: 2, job_title: 2 },
  },
  // s87 (F86-5): the executor section renders as a normal expandable list — Role/Name/
  // Email on the ribbon, Contact Details + Access & Permissions in the drawer. Person data
  // is canonical in generic_entries; the executor_access auth row is synced on save.
  [SECTION_SLUGS.LEGAL_YOUR_EXECUTOR]: {
    recordNoun: "executor",
    // RECORD-IDENTITY-1 (s199): identity leads, TYPE STAYS second. Charter §8.2 — identity does
    // not replace type; "identity + required facts" is the ordering.
    basicFieldKeys: ["name", "role", "email"],
    basicFieldWidths: {
      role: "flex",
      name: "flex",
      email: "flex", // s99 Fix 3: was "default" — capped track truncated the email.
    },
    groups: [
      // s223 (D-222.10): `told` — the interview's exec tiles, stored (FIELD-BEN-1: in a group, or the drawer hides it).
      { groupName: "Contact Details", fieldKeys: ["relationship", "told", "phone", "address"] },
      { groupName: "Access & Permissions", fieldKeys: ["access_level"] },
    ],
    valueHelp: {
      access_level: {
        "No access": "Executor cannot view any plan details",
        "Progress only": "Executor can see completion status but not your data",
        "Read access": "Executor can view all plan data you've entered",
      },
    },
    // s226 (CH1-ROW-NOTES-COLUMN-1): the row Note the interview writes; full-width in the drawer (FIELD-BEN-1).
    notesFieldKey: "notes",
  },
  [SECTION_SLUGS.ASSETS_VEHICLE_LIST]: {
    recordNoun: "vehicle",
    basicFieldKeys: ["vehicle_type", "make_model", "year", "value"],
    basicFieldWidths: {
      vehicle_type: "default",
      make_model: "flex",
      year: "narrow",
      value: "currency",
    },
    groups: [
      {
        groupName: "Registration",
        fieldKeys: [
          "plate",
          "vin",
          "registration_number",
          "registration_expiry",
          "registered_owner",
          "registration_type", // s172 Block C — FIELD-BEN-1: added with the spec field, same commit
        ],
      },
      {
        groupName: "Finance",
        fieldKeys: ["loan_balance", "has_loan", "loan_lender", "loan_lender_phone", "loan_lender_email", "loan_lender_address", "loan_lender_ask_for", "loan_lender_ask_for_phone", "loan_lender_ask_for_email", "loan_lender_ask_for_address", "loan_payment", "loan_co_borrower", "loan_co_borrower_phone", "loan_co_borrower_email", "loan_co_borrower_address"], // s235 PR A (rule 63): + the co-borrower's card details; s172 Block C — FIELD-BEN-1; s232 (chapter two): + the interview's loan answers
      },
      { groupName: "Insurance", fieldKeys: ["has_insurance", "policy_insurer", "policy_insurer_phone", "policy_insurer_email", "policy_insurer_address", "policy_insurer_ask_for", "policy_insurer_ask_for_phone", "policy_insurer_ask_for_email", "policy_insurer_ask_for_address", "policy_dealt_with", "policy_broker", "policy_broker_phone", "policy_broker_email", "policy_broker_address", "policy_broker_ask_for", "policy_broker_ask_for_phone", "policy_broker_ask_for_email", "policy_broker_ask_for_address"] }, // s235 PR B (D-235.20(4), rule 150) — FIELD-BEN-1
      // s232 (chapter two, ruling 1) — FIELD-BEN-1: "Left to someone?", added with the spec fields.
      { groupName: "Left to", fieldKeys: ["left_to", "left_to_person", "left_to_person_phone", "left_to_person_email", "left_to_person_address"] }, // s235 PR A (rule 63) — FIELD-BEN-1
      {
        groupName: "Storage",
        // s233 (D-233.37 point 5) — FIELD-BEN-1: `papers_location` added with the spec field, same commit.
        fieldKeys: ["key_location", "vehicle_location", "papers_location"],
      },
    ],
    notesFieldKey: "notes",
    fieldSpans: {
      plate: 1,
      vin: 1,
      registration_number: 1,
      registration_expiry: 1,
      registered_owner: 2,
      key_location: 2,
      vehicle_location: 2,
    },
  },
  [SECTION_SLUGS.ASSETS_PROPERTIES]: {
    basicFieldKeys: ["prop_type", "address", "value"],
    basicFieldWidths: {
      prop_type: "default",
      address: "flex",
      value: "currency",
    },
    groups: [
      // s172 Block C — FIELD-BEN-1: `mortgage_balance` added to the layout in the same commit as
      // the spec field. A field converged in generic_fields but absent here is invisible.
      // s232 (chapter two) — FIELD-BEN-1: + the interview's mortgage answers, and the two groups below.
      { groupName: "Mortgage", fieldKeys: ["has_mortgage", "mortgage_lender", "mortgage_lender_phone", "mortgage_lender_email", "mortgage_lender_address", "mortgage_lender_ask_for", "mortgage_lender_ask_for_phone", "mortgage_lender_ask_for_email", "mortgage_lender_ask_for_address", "mortgage_balance", "mortgage_payment", "mortgage_co_borrower", "mortgage_co_borrower_phone", "mortgage_co_borrower_email", "mortgage_co_borrower_address", "mortgage_insured", "mortgage_account_number"] },
      { groupName: "Insurance", fieldKeys: ["has_insurance", "policy_insurer", "policy_insurer_phone", "policy_insurer_email", "policy_insurer_address", "policy_insurer_ask_for", "policy_insurer_ask_for_phone", "policy_insurer_ask_for_email", "policy_insurer_ask_for_address", "policy_dealt_with", "policy_broker", "policy_broker_phone", "policy_broker_email", "policy_broker_address", "policy_broker_ask_for", "policy_broker_ask_for_phone", "policy_broker_ask_for_email", "policy_broker_ask_for_address"] }, // s235 PR B (D-235.20(4), rule 150) — FIELD-BEN-1
      { groupName: "Left to", fieldKeys: ["left_to", "left_to_person", "left_to_person_phone", "left_to_person_email", "left_to_person_address", "joint_gift_intent"] }, // s235 PR A (rule 63) — FIELD-BEN-1
      {
        groupName: "Title & Legal",
        fieldKeys: [
          "address",
          "title_ownership",
          // joint_owner_name is showIf-gated on title_ownership ∈ {Joint tenancy, Tenants in common} (D-154.3).
          "joint_owner_name",
          "joint_owner_phone", // s235 PR A (rule 63) — FIELD-BEN-1
          "joint_owner_email", // s235 PR A (rule 63) — FIELD-BEN-1
          "joint_owner_address", // s235 PR A (rule 63) — FIELD-BEN-1
          "roll_number",
          "legal_description",
          "property_form",
          "is_residence", // s234 (CH2-STORAGE-PR3) — FIELD-BEN-1: "Where you live"
          // property_use retired (D-154.1) — merged into prop_type; removed from the drawer group.
        ],
      },
      { groupName: "Access", fieldKeys: ["key_location", "entry_alarm_code", "mailbox_key_location", "safe_location", "safe_combination"] },
      {
        groupName: "Tenancy",
        fieldKeys: [
          "rented_out", // s234 (CH2-STORAGE-PR3) — FIELD-BEN-1; its answer (or Investment Property) opens the rest
          "tenant_name",
          "tenant_phone", // s235 PR A (rule 63) — FIELD-BEN-1
          "tenant_email", // s235 PR A (rule 63) — FIELD-BEN-1
          "tenant_address", // s235 PR A (rule 63) — FIELD-BEN-1
          "monthly_rent",
          "lease_end_date",
          "property_manager",
          "security_deposit",
          "last_month_deposit_taken", // s232 (chapter two) — FIELD-BEN-1
        ],
      },
    ],
    notesFieldKey: "notes",
    fieldSpans: {
      address: "full",
      key_location: 2,
      legal_description: 2,
      mailbox_key_location: 2,
      safe_location: 2,
      safe_combination: 2,
    },
    recordNoun: "property",
    recordNounPlural: "properties",
  },
  [SECTION_SLUGS.ASSETS_VALUABLES]: {
    recordNoun: "valuable",
    basicFieldKeys: ["category", "description", "value"],
    basicFieldWidths: { category: "default", description: "flex", value: "currency" },
    groups: [
      { groupName: "Location", fieldKeys: ["location"] },
      { groupName: "Left to", fieldKeys: ["left_to", "left_to_person", "left_to_person_phone", "left_to_person_email", "left_to_person_address"] }, // s232 (chapter two, ruling 1); s235 PR A (rule 63) — FIELD-BEN-1
      { groupName: "Firearm", fieldKeys: ["firearm_kind", "legal_taker", "legal_taker_phone", "legal_taker_email", "legal_taker_address", "in_locked_storage"] }, // s232 PR C (ruling 3); s235 PR A (rule 63) — FIELD-BEN-1
    ],
    notesFieldKey: "notes",
    fieldSpans: { location: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES]: {
    recordNoun: "policy",
    recordNounPlural: "policies",
    // RECORD-IDENTITY-1 part 2 (s199): identity leads, TYPE STAYS second. Charter §8.2 — identity
    // does not replace type; "identity + required facts" is the ordering.
    basicFieldKeys: ["provider", "type", "coverage_amount"],
    basicFieldWidths: { type: "default", provider: "flex", coverage_amount: "currency" },
    groups: [
      // s172 Block C — FIELD-BEN-1: `frequency` sits beside the premium it qualifies.
      { groupName: "Policy Details", fieldKeys: ["policy_num", "premium", "frequency", "renewal_date", "phone"] },
      // FIELD-AUDIT-1 (D-154.5 / #29): legacy `beneficiary` retired — structured trio.
      { groupName: "Beneficiary",    fieldKeys: ["designated_beneficiary", "beneficiary_name"] },
      { groupName: "Online Access",  fieldKeys: ["username", "account_email"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { beneficiary: 2, username: 2, beneficiary_name: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS]: {
    recordNoun: "account",
    // s101: ribbon leads with the bank identity; account_type 2nd (curation keys on it
    // via TOP_ZONE_CURATION_FIELD). balance + ownership promoted (both now required).
    // account_num drops to the Account Details drawer group.
    basicFieldKeys: ["bank", "account_type", "balance", "ownership"],
    basicFieldWidths: { bank: "flex", account_type: "default", balance: "currency", ownership: "default" },
    groups: [
      { groupName: "Account Details", fieldKeys: ["branch", "account_num"] },
      // 🔴 D-209.4 (s209): the FIELD-BEN-1 Beneficiary group is REMOVED from Bank Accounts. Both
      // fields are `isActive: false` in the spec as of s209 — a bank account carries no beneficiary
      // designation; `ownership` is what decides whether it skips the estate. The same group STAYS
      // on Investments (line ~371), Insurance Policies (line ~336) and Income & Pensions (line ~410),
      // where registered plans and policies genuinely carry designations.
      // joint_owner_name is showIf-gated on `ownership` containing "Joint" (DB-29);
      // it only renders when the ribbon ownership is a joint option.
      { groupName: "Ownership",        fieldKeys: ["joint_owner_name"] },
      { groupName: "Online Access",    fieldKeys: ["username", "account_email"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { account_num: 2, username: 2, joint_owner_name: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP]: {
    recordNoun: "investment",
    basicFieldKeys: ["inv_type", "institution", "account_num", "market_value"],
    basicFieldWidths: { inv_type: "default", institution: "flex", account_num: "default", market_value: "currency" },
    groups: [
      // FIELD-BEN-1 (s121): structured designation joins the free-text beneficiary here.
      // beneficiary_name is showIf-gated on designated_beneficiary === "Named person".
      // FIELD-AUDIT-1 (D-154.5 / #29): legacy `beneficiary` retired — trio only.
      { groupName: "Beneficiary",   fieldKeys: ["designated_beneficiary", "beneficiary_name"] },
      { groupName: "Ownership",      fieldKeys: ["ownership"] },
      { groupName: "Online Access",  fieldKeys: ["username", "account_email"] },
      // CRYPTO-CAPTURE-1 (s194, ruled by Reade): the six crypto fields were authored at s37,
      // transcribed into the spec ALREADY INACTIVE at s57 (f862ea14) and never ruled either way
      // (D-191.20). Reactivated at s194 — reactivate rather than redesign. They need homes here or
      // they converge into `generic_fields` and render NOWHERE (FIELD-BEN-1 / the s121 trap).
      //
      // 🔴 These two groups were shipped once before (245afdbd) and REVERTED (91056411) — correctly,
      // because the fields were inactive then and the entries were inert. That reason is now gone.
      // What must NOT come back with them is the same commit's `Arrangements`/`burial_cremation`
      // group, which double-rendered a field that has been the funeral-wishes header band since s99.
      //
      // Both groups SELF-SUPPRESS on non-crypto records: every member is `showIf`-gated on
      // `inv_type === "Cryptocurrency"`, and the renderer returns null for a group whose fields are
      // all hidden (ExpandableSectionLayout, the `groupFields.length === 0` guard).
      { groupName: "Crypto holding", fieldKeys: ["asset_name", "quantity", "approximate_value_cad"] },
      {
        groupName: "Crypto storage & recovery",
        fieldKeys: ["storage_type", "storage_location", "recovery_info_pointer"],
        // Ruled copy (Reade, s194). Rendered ABOVE the fields, before any input — `groupNote` is the
        // only drawer slot that is visible before typing: `field.instructions` does not render here
        // at all, and `valueHelp` renders below the input and only once a value exists.
        groupNote:
          "Your executor can only reach these holdings if they know where the recovery details are kept. Write the location only — never the recovery phrase or private key, which anyone could use to take the funds.",
      },
    ],
    notesFieldKey: "notes",
    fieldSpans: { beneficiary: 2, ownership: 2, username: 2, storage_location: 2, recovery_info_pointer: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS]: {
    recordNoun: "income source",
    basicFieldKeys: ["inc_type", "source", "amount", "frequency"],
    basicFieldWidths: { inc_type: "default", source: "flex", amount: "currency", frequency: "default" },
    groups: [
      // FIELD-AUDIT-1 #30 (D-154.5): pension_death_handling steers the survivor set + the trio;
      // legacy `beneficiary` retired for the structured trio.
      { groupName: "On death", fieldKeys: ["pension_death_handling"] },
      { groupName: "Survivor", fieldKeys: ["survivor_benefit", "survivor_recipient"] },
      { groupName: "Beneficiary", fieldKeys: ["designated_beneficiary", "beneficiary_name"] },
      { groupName: "Plan Details",            fieldKeys: ["plan_administrator", "start_date"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { survivor_recipient: 2, beneficiary: 2, plan_administrator: 2, beneficiary_name: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES]: {
    recordNoun: "debt",
    // s101: ownership promoted to the ribbon (now required); debt_insured stays in the drawer.
    // RECORD-IDENTITY-1 (s199): identity leads, type stays second.
    basicFieldKeys: ["creditor", "debt_type", "balance", "ownership"],
    basicFieldWidths: { debt_type: "default", creditor: "flex", balance: "currency", ownership: "default" },
    groups: [
      { groupName: "Loan Details", fieldKeys: ["account_num", "payment", "rate"] },
      // joint_owner_name is showIf-gated on `ownership` containing "Joint" (DB-29).
      { groupName: "Ownership",    fieldKeys: ["joint_owner_name"] },
      { groupName: "Liability",    fieldKeys: ["debt_insured"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { debt_insured: 2, joint_owner_name: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_HOUSING]: {
    recordNoun: "rental",
    basicFieldKeys: ["housing_type", "rental_address", "monthly_rent"],
    basicFieldWidths: { housing_type: "default", rental_address: "flex", monthly_rent: "currency" },
    groups: [
      { groupName: "Access",      fieldKeys: ["key_location", "entry_alarm_code"] },
      // s232 (chapter two) — FIELD-BEN-1: + the interview's answers (where you live, the co-tenant, a care residence's
      // name, the lease's kind, both deposits).
      { groupName: "Where you live", fieldKeys: ["is_residence", "residence_name", "residence_phone", "residence_email", "residence_address", "residence_ask_for", "residence_ask_for_phone", "residence_ask_for_email", "residence_ask_for_address"] }, // s235 PR A (rule 63)
      { groupName: "Landlord",    fieldKeys: ["landlord", "landlord_phone", "landlord_email", "landlord_address", "landlord_ask_for", "landlord_ask_for_phone", "landlord_ask_for_email", "landlord_ask_for_address", "co_tenant", "co_tenant_phone", "co_tenant_email", "co_tenant_address"] }, // s235 PR A (rule 63)
      { groupName: "Lease Terms", fieldKeys: ["lease_term", "lease_start", "lease_end", "security_deposit", "last_month_deposit_paid", "prepaid_deposit"] },
      { groupName: "Ownership",   fieldKeys: ["ownership"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { rental_address: "full", key_location: 2, landlord: 2, landlord_email: 2, ownership: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_TAXES]: {
    recordNoun: "tax record",
    // RECORD-IDENTITY-1 (s199): identity leads, type stays second. `tax_year` is NOT required, so
    // a record can carry no identity — it then renders the section's record noun in ITALICS, which
    // is correct and already live. Do not add a fallback-to-type; that reintroduces the bug.
    basicFieldKeys: ["tax_year", "tax_type", "status", "amount"],
    basicFieldWidths: { tax_type: "flex", tax_year: "narrow", status: "default", amount: "currency" },
    groups: [
      // DB-10: where the executor finds the records — the fact that matters most.
      { groupName: "Records", fieldKeys: ["records_location"] },
      { groupName: "Accountant / Preparer", fieldKeys: ["accountant_name", "accountant_phone", "accountant_email"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { records_location: "full", accountant_name: 2, accountant_phone: 2, accountant_email: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_BUSINESS_SELF_EMPLOYMENT]: {
    recordNoun: "business",
    // RECORD-IDENTITY-1 (s199): identity leads, type stays second.
    basicFieldKeys: ["biz_name", "biz_type", "value"],
    basicFieldWidths: { biz_type: "default", biz_name: "flex", value: "currency" },
    groups: [
      // s237 chapter three PR 2 (FIELD-BEN-1): own business or self-employed.
      { groupName: "Business Details", fieldKeys: ["role", "business_kind"] },
      // FIELD-BEN-1 (s204, PEOPLE-RECORD-1): joint_owner_name added in the SAME commit as the
      // spec field. A field converged in generic_fields but absent from this group is invisible
      // in the drawer. Unlike the other three joint_owner_name sites it is UNGATED — this section
      // has no `ownership` select to gate on (D-172.6 retired it) — so it always renders.
      // s237 chapter three PR 2 (FIELD-BEN-1, D-235.73): the succession contact's and every co-owner's card details.
      { groupName: "Succession",       fieldKeys: ["succession_agreement", "agreement_location", "succession_contact", "succession_contact_phone",
        "succession_contact_email", "succession_contact_address", "joint_owner_name", "joint_owner_phone", "joint_owner_email", "joint_owner_address",
        "co_owner_2", "co_owner_2_phone", "co_owner_2_email", "co_owner_2_address", "co_owner_3", "co_owner_3_phone", "co_owner_3_email", "co_owner_3_address"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { role: 2, agreement_location: 2, succession_contact: 2, joint_owner_name: 2 },
  },
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_PRE_AUTHORIZED_DEBITS]: {
    recordNoun: "recurring service",
    basicFieldKeys: ["category", "service_name", "amount", "frequency"],
    basicFieldWidths: { category: "default", service_name: "flex", amount: "currency", frequency: "default" },
    groups: [
      { groupName: "Payment & Auto-Pay", fieldKeys: ["is_autopay", "payment_method_type", "account_or_member_number"] },
      { groupName: "Online Access",       fieldKeys: ["login_username", "account_email", "login_url"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { account_or_member_number: 2, login_username: 2, account_email: 2, login_url: 2 },
  },
  // s108 (DB-S5): unified C&D section. One row per child/dependent; every topic
  // lives in a gated drawer group (gate yes_no field → per-field showIf on the
  // group's content, the Bank Accounts joint_owner_name pattern). Per-group notes,
  // so the section-level notesFieldKey is null. Replaces the 9-section cascade.
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW]: {
    recordNoun: "dependant",
    basicFieldKeys: ["first_name", "surname", "dob", "relationship"], // BENEFICIARY-FILL-1 (s156): name split
    basicFieldWidths: { first_name: "flex", surname: "flex", dob: "default", relationship: "default" },
    groups: [
      // s221 Lane 1 (D-221.10): person_kind is the one section's kind column (FIELD-BEN-1: a field
      // absent from the layout config is invisible in the drawer).
      { groupName: "Identity", fieldKeys: ["person_kind", "special_needs", "special_needs_detail", "citizenship", "identity_notes"] },
      // s221 CONTACT-CARD-ONE-1: the person's own contact details (FIELD-BEN-1 — or the drawer hides them).
      { groupName: "Contact details", fieldKeys: ["person_phone", "person_email", "person_address"] },
      // FIELD-AUDIT-1 (D-154.5): explicit Primary / Backup Guardian sub-headers (layout only).
      // s221 Lane 1 (D-221.11): guardian_named_in_will sits with the gate, ungated, so it shows even
      // when no guardian is named yet.
      { groupName: "Guardianship", fieldKeys: ["has_guardianship_info", "guardian_named_in_will"] },
      { groupName: "Primary Guardian", fieldKeys: ["primary_guardian", "primary_guardian_address", "primary_guardian_phone", "primary_guardian_email"] },
      { groupName: "Backup Guardian", fieldKeys: ["backup_guardian", "backup_guardian_address", "backup_guardian_phone", "backup_guardian_email"] },
      { groupName: "Guardianship Details", fieldKeys: ["criteria", "guardianship_notes"] },
      { groupName: "Health & Medical", fieldKeys: ["has_health_info", "conditions", "allergies", "medications", "doctor_name", "doctor_address", "doctor_phone", "doctor_email", "coverage", "health_notes"] },
      { groupName: "Day-to-day Care", fieldKeys: ["has_care_info", "routines", "raised_in_faith", "religion", "care_notes"] },
      { groupName: "Education & Activities", fieldKeys: ["has_education_info", "school_name", "school_address", "school_phone", "school_email", "school_contact_person", "school_contact_phone", "school_contact_email", "school_contact_address", "grade", "learning_needs", "activities", "education_notes"] },
      { groupName: "Financial Support", fieldKeys: ["has_financial_info", "trustee", "trustee_address", "trustee_phone", "trustee_email", "guardian_comp", "resp", "rdsp", "funding_details", "inheritance_instructions", "financial_notes"] },
      { groupName: "Other", fieldKeys: ["other_notes", "values_wishes"] },
    ],
    notesFieldKey: null,
    fieldSpans: {
      special_needs_detail: "full",
      identity_notes: "full",
      primary_guardian_address: "full",
      backup_guardian_address: "full",
      person_address: "full",
      school_contact_address: "full",
      criteria: "full",
      guardianship_notes: "full",
      doctor_address: "full",
      health_notes: "full",
      care_notes: "full",
      school_address: "full",
      education_notes: "full",
      trustee_address: "full",
      funding_details: "full",
      inheritance_instructions: "full",
      financial_notes: "full",
      other_notes: "full",
      values_wishes: "full",
    },
  },
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_HEALTH]: {
    recordNoun: "health record",
    basicFieldKeys: ["name", "conditions"],
    basicFieldWidths: { name: "default", conditions: "flex" },
    groups: [
      { groupName: "Medical", fieldKeys: ["allergies", "medications"] },
      { groupName: "Care Team", fieldKeys: ["doctor_name", "doctor_phone", "doctor_address", "coverage"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { allergies: 2, medications: 2, doctor_address: 2 },
  },
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_DAILY_CARE]: {
    recordNoun: "care record",
    basicFieldKeys: ["name", "routines"],
    basicFieldWidths: { name: "default", routines: "flex" },
    groups: [
      { groupName: "Routine & Culture", fieldKeys: ["religion"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { religion: 2 },
  },
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_EDUCATION]: {
    recordNoun: "education record",
    basicFieldKeys: ["name", "school_name", "grade"],
    basicFieldWidths: { name: "default", school_name: "flex", grade: "narrow" },
    groups: [
      { groupName: "School", fieldKeys: ["school_address", "school_phone", "school_contact_person"] },
      { groupName: "Learning", fieldKeys: ["learning_needs", "activities"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { school_address: "full", school_phone: 1, school_contact_person: 2, learning_needs: 2, activities: 2 },
  },
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_GUARDIANSHIP]: {
    recordNoun: "guardianship plan",
    recordNounPlural: "guardianship plans",
    basicFieldKeys: ["name", "primary_guardian"],
    basicFieldWidths: { name: "default", primary_guardian: "flex" },
    groups: [
      {
        groupName: "Primary Guardian",
        fieldKeys: ["primary_guardian_address", "primary_guardian_phone", "primary_guardian_email"],
        groupNote:
          "Naming a guardian here helps the people who'll need it understand your wishes. To make sure it's followed, name this guardian in your will too.",
      },
      { groupName: "Backup Guardian", fieldKeys: ["backup_guardian", "backup_guardian_address", "backup_guardian_phone", "backup_guardian_email"] },
      { groupName: "Expectations", fieldKeys: ["criteria"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { primary_guardian_address: "full", primary_guardian_email: 2, backup_guardian: 2, backup_guardian_address: "full", backup_guardian_email: 2, criteria: "full" },
    // CD-D1: copy guardian setup from another child (snapshot, not a live link).
    // Never copies `name` (cascade-owned) or `notes`.
    copyFromSibling: {
      label: "Copy guardian details from…",
      fields: [
        "primary_guardian", "primary_guardian_address", "primary_guardian_phone", "primary_guardian_email",
        "backup_guardian", "backup_guardian_address", "backup_guardian_phone", "backup_guardian_email",
        "criteria",
      ],
    },
  },
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_OTHER]: {
    recordNoun: "consideration",
    basicFieldKeys: ["name", "topic"],
    basicFieldWidths: { name: "default", topic: "flex" },
    groups: [
      { groupName: "Details", fieldKeys: ["details"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { details: "full" },
  },
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_FINANCIAL_SUPPORT]: {
    recordNoun: "arrangement",
    basicFieldKeys: ["name", "trustee"],
    // Both basic fields flex → balanced 50/50 collapsed row (Child Name | Trustee).
    // Lesson: when a field is promoted to basicFieldKeys, set its width explicitly
    // rather than leaning on the "default" fixed slot (which renders cramped here).
    basicFieldWidths: { name: "flex", trustee: "flex" },
    groups: [
      {
        groupName: "Trustee",
        fieldKeys: ["trustee_address", "trustee_email", "trustee_phone"],
        groupNote:
          "Recording a trustee here is for your executor's reference. Be sure this trustee is also named in your will or trust document.",
      },
      { groupName: "Education / Disability Funding", fieldKeys: ["resp", "rdsp", "funding_details"] },
      { groupName: "Inheritance", fieldKeys: ["inheritance_instructions", "guardian_comp"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { trustee_address: "full", trustee_email: 2, funding_details: 2, inheritance_instructions: "full" },
  },
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_VALUES_WISHES]: {
    recordNoun: "letter",
    basicFieldKeys: ["name", "occasion"],
    basicFieldWidths: { name: "default", occasion: "flex" },
    // The section's `file` (file_upload) field is intentionally omitted from any
    // group: file_upload has no EditableCell branch, so it would render as a dead
    // text input in the drawer. Per-record uploads go through the drawer's
    // "Upload Supporting Documents" section instead (session 68 RT#2 decision).
    groups: [],
    notesFieldKey: "notes",
  },
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_GOV_IDS]: {
    recordNoun: "ID",
    basicFieldKeys: ["name", "id_type", "expiry"],
    basicFieldWidths: { name: "flex", id_type: "default", expiry: "default" },
    groups: [
      // id_number is Cat A → renderCell routes it through SensitiveCell (masked).
      // expiry promoted to the collapsed row (CD-13) — dropped from this group to
      // avoid a double-render (basic + drawer).
      { groupName: "ID Details", fieldKeys: ["id_number", "issuing_authority"] },
      { groupName: "Storage", fieldKeys: ["location"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { issuing_authority: 2, location: 2 },
  },
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_LETTERS]: {
    recordNoun: "letter",
    basicFieldKeys: ["recipient", "relationship"],
    basicFieldWidths: { recipient: "flex", relationship: "default" },
    // `letter_file` (file_upload) is intentionally omitted — file_upload renders
    // as a dead text input in the drawer (no EditableCell branch); the letter is
    // uploaded via the drawer's "Upload Supporting Documents" section instead
    // (allowDocuments: true). Mirrors Values & Wishes (session 68 D6).
    groups: [
      { groupName: "Recipient", fieldKeys: ["recipient_address", "recipient_phone", "recipient_email"] },
      { groupName: "Letter", fieldKeys: ["date_written"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { date_written: 1, recipient_address: 2 },
  },
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]: {
    recordNoun: "preference",
    recordNounPlural: "preferences",
    // F86-7: `wishes` (required) promoted to the collapsed ribbon (flex — long
    // multiline, renders single-row compact). Dropped from the now-empty "Wishes"
    // drawer group to avoid a double-render.
    // 🔴 s192 (D-192.1) HEADER-MOUNT-SPECIFY-1: `burial_cremation` was the section-header band
    // from s99 until s192. It is PROMOTED to the ribbon because the header band mounts
    // `EditableCell` only — it carries no `SpecifyOnOtherInput`, so the field's
    // `specifyOnOther: true` reveal fired nowhere and choosing "Other" asked a question the
    // product never let anyone answer. On the ribbon the existing per-row specify path (the
    // basics-in-drawer block in ExpandableSectionLayout) carries it. Capped width: two flex
    // columns already compete here, a third over-packs.
    //
    // 🔴 ORDER IS LOAD-BEARING — `topic` MUST stay first (D-192.3). Two reasons, and the second
    // is not obvious: (1) Layer-3 read a position-1 burial select as turning the whole section
    // into a burial questionnaire; (2) this section has NO `RECORD_FIELD_BY_SECTION` override,
    // so `basicFieldKeys[0]` IS the record label — `section-data-preview.ts` falls back to it.
    // Shipping burial first silently relabelled every collapsed row and the section's preview
    // line from the planner's topic ("Religion / Faith") to their burial answer ("Cremation").
    // Moving anything ahead of `topic` here changes what the record is CALLED, everywhere.
    basicFieldKeys: ["topic", "burial_cremation", "wishes", "prepaid"],
    basicFieldWidths: { burial_cremation: "default", topic: "flex", wishes: "flex", prepaid: "default" },
    groups: [
      // FIELD-AUDIT-1 #15 (D-154.5): contact → funeral_home_name (shown when prepaid=Yes).
      { groupName: "Vendor", fieldKeys: ["funeral_home_name", "contact_phone", "contact_email", "contact_address"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { contact: 2, contact_address: 2 },
  },
  // s88: the WAT "Anything else" band in Where Is Everything renders through the shared
  // ExpandableSectionLayout (via ExpandableSectionStandalone), so it needs a config entry.
  // Ribbon = Category · Item · Location; drawer = Address + Contact + Notes. The section is
  // hidden from the category surface (HIDDEN_SECTIONS) and only mounts inside WIE.
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY]: {
    recordNoun: "item",
    basicFieldKeys: ["category", "item", "location"],
    basicFieldWidths: { category: "default", item: "flex", location: "flex" },
    groups: [
      { groupName: "Address", fieldKeys: ["address"] },
      { groupName: "Contact", fieldKeys: ["contact_name", "contact_phone", "contact_email"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { address: "full", contact_name: 2 },
  },
  [SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS]: {
    recordNoun: "document",
    // `location` (now a basic field) is the Where-Is-Everything aggregate_role='location'
    // source; the aggregator reads it regardless of layout/position, so WIE keeps working.
    basicFieldKeys: ["doc_type", "location", "date_signed"],
    basicFieldWidths: { doc_type: "flex", location: "flex", date_signed: "default" },
    groups: [
      // s233 (D-233.37 point 2) — FIELD-BEN-1: `last_updated` added with the spec field, same commit.
      { groupName: "Dates", fieldKeys: ["expires", "last_updated"] },
    ],
    notesFieldKey: "notes",
  },
  [SECTION_SLUGS.LEGAL_POWERS_OF_ATTORNEY]: {
    recordNoun: "power of attorney",
    recordNounPlural: "powers of attorney",
    // s101: attorney (now required) promoted to the ribbon beside the type; location
    // kept; date_executed moves to the drawer. substitute_attorney stays in the drawer.
    basicFieldKeys: ["poa_type", "attorney", "location"],
    basicFieldWidths: { poa_type: "flex", attorney: "flex", location: "flex" },
    groups: [
      { groupName: "Details", fieldKeys: ["date_executed", "substitute_attorney"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { substitute_attorney: 2 },
  },
  // Keyed by the literal slug (not a SECTION_SLUGS constant): "beneficiaries" is a
  // common word that collides with non-slug literals (the recordNounPlural below,
  // a will-analysis schema key), so promoting it to a constant trips check:slugs.
  // The renderer looks up EXPANDABLE_LAYOUT_CONFIG[section.slug] — identical lookup.
  "beneficiaries": {
    recordNoun: "beneficiary",
    recordNounPlural: "beneficiaries",
    // beneficiary_share (multiline) promoted to the collapsed row; renders single-row
    // there per the s64 EditableCell fix. The now-empty "Bequest" group is dropped.
    // s95: beneficiary_type (Person/Charity/Organization) added to the ribbon beside
    // the name — at-a-glance entity type. s128 (BINDING-AUDIT-1): beneficiary_share
    // reactivated, so the effective ribbon is name · type · relationship · share.
    basicFieldKeys: ["beneficiary_first_name", "beneficiary_surname", "beneficiary_type", "beneficiary_relationship", "beneficiary_share"], // BENEFICIARY-FILL-1 (s156): name split
    basicFieldWidths: { beneficiary_first_name: "flex", beneficiary_surname: "flex", beneficiary_type: "default", beneficiary_relationship: "flex", beneficiary_share: "flex" },
    groups: [
      // s223 (D-222.10): the interview's `benef` answers, in a group (FIELD-BEN-1) or the drawer hides them.
      { groupName: "Details", fieldKeys: ["name_in_will", "beneficiary_tile", "beneficiary_who_they_are", "beneficiary_minor", "beneficiary_dob", "beneficiary_class"] }, // FIELD-AUDIT-1 (D-154.5)
      { groupName: "Contact", fieldKeys: ["beneficiary_address", "beneficiary_phone", "beneficiary_email", "beneficiary_ask_for", "beneficiary_ask_for_phone", "beneficiary_ask_for_email", "beneficiary_ask_for_address"] }, // s236: a charity's person to ask for (rule 150)
    ],
    notesFieldKey: "notes",
    fieldSpans: { beneficiary_address: "full" },
  },
  [SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS]: {
    recordNoun: "contact",
    // s101: phone (now required) + email promoted to the ribbon; firm moves to the drawer.
    // RECORD-IDENTITY-1 (s199): identity leads, type stays second.
    basicFieldKeys: ["name", "role", "phone", "email"],
    basicFieldWidths: { role: "flex", name: "flex", phone: "default", email: "flex" },
    groups: [
      { groupName: "Details", fieldKeys: ["firm"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { firm: 2 },
  },
  [SECTION_SLUGS.LEGAL_BUSINESS_DOCUMENTS]: {
    recordNoun: "document",
    basicFieldKeys: ["document", "location", "date_info"],
    basicFieldWidths: { document: "flex", location: "flex", date_info: "default" },
    groups: [
      { groupName: "Details", fieldKeys: ["funded"] },
    ],
    notesFieldKey: "notes",
  },
  [SECTION_SLUGS.LEGAL_BUSINESS_CONTACTS]: {
    recordNoun: "contact",
    // RECORD-IDENTITY-1 part 2 (s199): identity leads, type ("role") stays second.
    basicFieldKeys: ["name", "role", "firm"],
    basicFieldWidths: { role: "flex", name: "flex", firm: "flex" },
    groups: [
      { groupName: "Details", fieldKeys: ["phone", "email"] },
    ],
    notesFieldKey: "notes",
  },
  // ── Personal Information (s72 expandable rollout) ────────────────────────────
  [SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS]: {
    recordNoun: "ID",
    recordNounPlural: "IDs",
    // `location` (aggregate_role='location') stays a basic field; the WIE aggregator
    // reads it by tag regardless of layout. F86-7: id_number is Cat A (password) AND
    // required → promoted to the collapsed ribbon, where renderCell routes it through
    // SensitiveCell (masked). Dropped from the now-empty "ID Number" drawer group.
    // s101: expiry dropped from the ribbon to the drawer to tighten the row.
    // s108 (DB-S5): person_name leads the ribbon so each row says whose ID it is
    // (the planner's own, a child's, etc.) — child Gov IDs now live here instead of
    // the retired children-dependents-gov-ids section.
    basicFieldKeys: ["person_name", "id_type", "location", "id_number"],
    // s101: rebalance — id_type capped (was flex, hogging width → wide gap before
    // location); location + id_number keep the flex (id_number holds the Cat A
    // "Open your secure file" affordance, so it must stay wide). Trade-off: the longest
    // id_type option ("Social Insurance Number (SIN)") wraps to two lines.
    basicFieldWidths: { person_name: "default", id_type: "default", location: "flex", id_number: "flex" },
    groups: [
      { groupName: "Dates", fieldKeys: ["expiry"] },
    ],
    notesFieldKey: "notes",
  },
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_MEDICAL]: {
    recordNoun: "entry",
    recordNounPlural: "entries",
    // Only two non-notes fields → a 2-field collapsed row, no drawer groups.
    basicFieldKeys: ["category", "details"],
    // category is flex: select options ("Current Medications", "Organ Donor
    // Status") truncate at the capped `default` track.
    basicFieldWidths: { category: "flex", details: "flex" },
    groups: [],
    notesFieldKey: "notes",
    // s95 (Option 2): Organ Donor Status is a single fact the executor needs in the
    // first hours — rendered above the record list, not buried as one row among many.
    headerField: {
      fieldKey: "organ_donor_status",
      label: "Organ Donor Status",
      helpText:
        "Whether you're a registered organ/tissue donor — your care team and executor may need this within hours.",
    },
  },
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_INSURANCE]: {
    recordNoun: "policy",
    recordNounPlural: "policies",
    basicFieldKeys: ["provider", "policy_number", "plan_type"],
    basicFieldWidths: { provider: "flex", policy_number: "default", plan_type: "default" },
    groups: [
      { groupName: "Coverage", fieldKeys: ["coverage", "contact_phone"] },
      // 🔴 The `password` field on THIS section was switched off in the first s207 deploy and its
      // key was removed from this group at s208 (D-196.1 act 2). The comment that stood here said
      // "password is Cat A → masked via SensitiveCell in the drawer", which described a key that is
      // no longer in the array. `password` is still a live field on OTHER sections — the password
      // log and the device/account rows below — so the `CellInputType` union and those groups stay.
      { groupName: "Login", fieldKeys: ["username", "account_email"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { coverage: 2, username: 2 },
  },
  [SECTION_SLUGS.FAMILY_FRIENDS_AND_PETS_IMPORTANT_CONTACTS]: {
    recordNoun: "contact",
    // Layout conversion only — the Unified Contacts field standardization stays parked.
    basicFieldKeys: ["name", "relationship", "phone"],
    basicFieldWidths: { name: "flex", relationship: "default", phone: "default" },
    groups: [
      { groupName: "Details", fieldKeys: ["email", "address"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { email: 2, address: "full" },
  },
  [SECTION_SLUGS.FAMILY_PETS]: {
    recordNoun: "pet",
    // s224 (D-196.1 act b, D-223.36): `row_kind` is basic — the planner page's own row has to say
    // whether it is a pet or the vet, the same way the children page shows `person_kind`.
    basicFieldKeys: ["row_kind", "pet_name", "pet_type", "guardian"],
    basicFieldWidths: { row_kind: "default", pet_name: "flex", pet_type: "default", guardian: "flex" },
    groups: [
      { groupName: "Care", fieldKeys: ["breed", "insurance"] },
      // s224 CONTACT-CARD-ONE-1: the guardian reads as a full contact, like every other card.
      { groupName: "Who would take them", fieldKeys: ["guardian_phone", "guardian_email", "guardian_address"] },
      // 🔴 s224 (D-223.36 ruling 2): the old group was `vet` · `vet_phone` · `vet_email` ·
      // `vet_address` — four RETIRED columns that sat on every pet row. The vet is its own row now,
      // and these are that row's fields. Retired fields are omitted from the layout by convention.
      { groupName: "Your vet", fieldKeys: ["vet_practice", "vet_contact", "vet_contact_phone", "vet_contact_email", "vet_contact_address", "vet_ask_for", "vet_ask_for_phone", "vet_ask_for_email", "vet_ask_for_address"] }, // s236: + the person to ask for (rule 150)
    ],
    notesFieldKey: "notes",
    fieldSpans: { vet_practice: 2, vet_contact: 2, vet_contact_email: 2, vet_contact_address: "full", guardian_email: 2, guardian_address: "full", insurance: 2 },
  },
  // ── Online Presence (s73 expandable rollout — completes the table→expandable
  //    migration; zero multi-record tables remain). Cat A credential fields
  //    (password/pin always; username only where is_category_a=true) live in the
  //    Login drawer group → masked via SensitiveCell, never on the collapsed row.
  //    Inactive template fields are omitted here. ──────────────────────────────
  [SECTION_SLUGS.PASSWORD_LOG_DEVICES_LOCAL_ACCESS]: {
    recordNoun: "device",
    // username is NOT Cat A here → stays a basic field. location (aggregate_role=
    // 'location') stays basic so the WIE aggregator reads it regardless of layout.
    // F86-7: `password` (required, Cat A) promoted to the ribbon — renderCell routes
    // it through SensitiveCell (masked) on the collapsed row too; dropped from the
    // now-empty "Login" drawer group.
    basicFieldKeys: ["site_app", "username", "location", "password"],
    basicFieldWidths: { site_app: "flex", username: "default", location: "flex", password: "flex" },
    groups: [
      { groupName: "Device", fieldKeys: ["make_model"] },
    ],
    notesFieldKey: "notes_docs",
    fieldSpans: { make_model: 2 },
  },
  [SECTION_SLUGS.PASSWORD_LOG_PRIMARY_EMAIL_IDENTITY_ACCOUNTS]: {
    recordNoun: "account",
    // username is NOT Cat A here → basic. F86-7: `password` (required, Cat A) promoted
    // to the ribbon (masked via SensitiveCell); `pin` (Cat A, not required) stays in
    // the Login drawer group.
    basicFieldKeys: ["site_app", "username", "account_email", "password"],
    basicFieldWidths: { site_app: "flex", username: "flex", account_email: "flex", password: "flex" },
    groups: [
      { groupName: "Login", fieldKeys: ["pin"] },
    ],
    notesFieldKey: "notes_docs",
  },
  [SECTION_SLUGS.PASSWORD_LOG_GOVERNMENT_TAX]: {
    recordNoun: "account",
    // username IS Cat A here → drawer-only. account_number is not Cat A → basic.
    basicFieldKeys: ["site_app", "account_number", "account_email"],
    basicFieldWidths: { site_app: "flex", account_number: "default", account_email: "flex" },
    groups: [
      { groupName: "Login", fieldKeys: ["username", "password", "pin"] },
    ],
    notesFieldKey: "notes_docs",
    fieldSpans: { username: 2 },
  },
  [SECTION_SLUGS.PASSWORD_LOG_ONLINE_ACCOUNTS_COMMUNITIES]: {
    recordNoun: "account",
    // password/pin are Cat A → drawer-masked; username normalized non-Cat-A (FIELD-AUDIT-1 D-154.5).
    // account_email (non-Cat-A, the recovery/notification address) joins site_app on the collapsed row.
    basicFieldKeys: ["site_app", "account_email"],
    basicFieldWidths: { site_app: "flex", account_email: "flex" },
    groups: [
      { groupName: "Login", fieldKeys: ["username", "password", "pin"] },
      { groupName: "Instructions", fieldKeys: ["executor_instructions"] }, // FIELD-AUDIT-1 (D-152.11 rider)
    ],
    notesFieldKey: "notes_docs",
    fieldSpans: { username: 2 },
  },
  // s171: PASSWORD_LOG_SHOPPING_LOYALTY_PROGRAMS config removed — the section was retired in
  // FIELD-AUDIT-1 (D-152.11) (isActive:false + defaultRows:null) and folded into Online Accounts
  // & Communities. The renderer looks this map up by slug, so an entry for a section that never
  // renders is dead config that reads as live support for a live section.
  [SECTION_SLUGS.PASSWORD_LOG_DIGITAL_FILES_CLOUD_STORAGE]: {
    recordNoun: "location",
    // No Cat A fields. provider + account_email identify the record on the row;
    // the descriptive fields group in the drawer. Notes field key is "notes".
    basicFieldKeys: ["provider", "account_email"],
    basicFieldWidths: { provider: "flex", account_email: "flex" },
    groups: [
      { groupName: "Details", fieldKeys: ["contents_summary", "executor_instructions"] },
    ],
    notesFieldKey: "notes",
    fieldSpans: { contents_summary: "full", executor_instructions: "full" },
  },
};
